/**
 * Manage stock — design/PROTOTYPE-SPEC.md §6.
 *
 * Products grouped by classification with inline row editing, the two taxonomy
 * editors, and the import/export toolbar.
 *
 * ## Why this page groups by classification
 *
 * The prototype groups, and it is right to: a clinic thinks in "injectables"
 * and "retail", and the two have genuinely different columns that matter —
 * retail price is meaningless on a numbing cream and load-bearing on a serum.
 * A flat table sorted by name puts a £180 collagen stimulator next to a £8
 * microneedling cartridge and invites exactly the mis-edit this page must not
 * make easy.
 *
 * Editing is manager-and-up (`manageProducts`), reading is not: a staff member
 * cannot log usage against a product they cannot see, but they have no business
 * changing what it costs. See docs/DECISIONS.md §2.
 *
 * The import result box is deliberately detailed. "Imported 12 products" alone
 * leaves someone staring at a sheet of 40 rows wondering about the other 28, and
 * the most common cause — the names already existed — is reassuring once said
 * out loud.
 */
import { can, type Role as CoreRole } from '@inventory-tracker/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  archiveProduct,
  createClassification,
  createLocation,
  deleteClassification,
  deleteLocation,
  importProducts,
  listClassifications,
  listLocations,
  listProducts,
  updateClassification,
  updateLocation,
  updateProduct,
  type Classification,
  type ImportResult,
  type ProductRow,
  type StockLocation,
  type UpdateProductInput,
} from '../lib/api.js';
import { c, primaryButton, sans } from '../theme.js';
import { cardStyle, money, smallButton } from './format.js';
import { ProductRowEditor } from './ProductRowEditor.js';
import { downloadTemplate, exportProducts, readSpreadsheet, toImportRows } from './spreadsheet.js';
import { TaxonomyEditor } from './TaxonomyEditor.js';

interface Props {
  clinicName: string;
  role: CoreRole;
  onChanged: () => void;
  refreshToken: number;
}

type Busy = 'template' | 'import' | 'export' | null;

/**
 * Products under their classification heading, in the taxonomy's own sort
 * order — which is a clinic's own ordering, not alphabetical, so it must be
 * preserved rather than re-sorted here.
 *
 * A product whose classification was deleted or never set still has to appear
 * somewhere: dropping it would hide a real product from the only page that can
 * fix it. It goes last, under a heading that says what is wrong.
 */
export function groupByClassification(
  rows: ProductRow[],
  classifications: Classification[],
): { id: string | null; label: string; rows: ProductRow[] }[] {
  const groups: { id: string | null; label: string; rows: ProductRow[] }[] = classifications.map((cl) => ({
    id: cl.id,
    label: cl.label,
    rows: rows.filter((r) => r.classificationId === cl.id),
  }));

  const known = new Set(classifications.map((cl) => cl.id));
  const orphans = rows.filter((r) => r.classificationId === null || !known.has(r.classificationId));
  if (orphans.length > 0) groups.push({ id: null, label: 'Not classified', rows: orphans });

  return groups.filter((g) => g.rows.length > 0);
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '9px 12px',
  fontSize: 12.5,
  textTransform: 'lowercase',
  color: c.muted2,
  fontWeight: 400,
};

export function ManagePage({ clinicName, role, onChanged, refreshToken }: Props) {
  const mayEdit = can(role, 'manageProducts');
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [rows, setRows] = useState<ProductRow[]>([]);
  /** The classification group a dragged product is currently over. */
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<(ImportResult & { fileName: string }) | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * One reload for all three lists. They are shown together and edited
   * together — moving a product to another classification changes both the row
   * and the group it sits in — so refreshing them independently would let the
   * page render a product under a heading it no longer belongs to.
   */
  const reload = useCallback(async () => {
    const [p, cl, loc] = await Promise.all([
      listProducts(),
      listClassifications(),
      listLocations(),
    ]);
    setRows(p.products);
    setClassifications(cl.classifications);
    setLocations(loc.locations);
  }, []);

  useEffect(() => {
    void reload().catch((e: unknown) =>
      setError(e instanceof Error ? e.message : 'Could not load your products'),
    );
    // refreshToken is a dependency, not a value — see AppShell's header note.
  }, [reload, refreshToken]);

  /**
   * Every mutation reloads and then tells the shell. The reload is what keeps
   * the group headings honest; `onChanged` is what keeps the sidebar's reorder
   * badge and the dashboard's stock value from disagreeing with this page after
   * a min-level or cost edit.
   */
  async function mutate(work: () => Promise<unknown>): Promise<void> {
    await work();
    await reload();
    onChanged();
  }

  const saveProduct = (id: string, input: UpdateProductInput) =>
    mutate(() => updateProduct(id, input));
  const archive = (id: string) => mutate(() => archiveProduct(id));

  /** Every category already in use, for the category list when editing. */
  const categories = [
    ...new Map(
      rows
        .map((r) => r.category?.trim())
        .filter((name): name is string => Boolean(name))
        .map((name) => [name.toLowerCase(), name]),
    ).values(),
  ].sort((a, b) => a.localeCompare(b));

  /**
   * Dropping a product on another type's group moves it there. The same
   * `PATCH` the Edit drop-down sends, so it is audited the same way; dragging
   * is a shortcut, not a second code path. Touch screens and keyboards still
   * have Edit.
   */
  async function dropOn(classificationId: string | null) {
    const productId = dragging;
    setDropTarget(null);
    setDragging(null);
    if (!productId || !classificationId) return;
    const row = rows.find((r) => r.id === productId);
    if (!row || row.classificationId === classificationId) return;
    setError(null);
    try {
      await saveProduct(productId, { classificationId });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not move that product');
    }
  }

  async function onTemplate() {
    setBusy('template');
    setError(null);
    try {
      await downloadTemplate();
    } catch {
      setError('Could not build the template file');
    } finally {
      setBusy(null);
    }
  }

  async function onExport() {
    setBusy('export');
    setError(null);
    try {
      await exportProducts(rows, clinicName);
    } catch {
      setError('Could not build the export file');
    } finally {
      setBusy(null);
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset immediately so choosing the same file twice still fires a change.
    e.target.value = '';
    if (!file) return;

    setBusy('import');
    setError(null);
    setResult(null);
    try {
      const raw = await readSpreadsheet(file);
      const importRows = toImportRows(raw);

      if (importRows.length === 0) {
        setError(
          `${file.name} had no rows with a product name. Check the first row holds your column headings.`,
        );
        return;
      }

      const res = await importProducts(importRows, file.name);
      setResult({ ...res, fileName: file.name });
      await reload();
      onChanged();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Could not read ${file.name}. Use a .csv, .xlsx or .xls file`,
      );
    } finally {
      setBusy(null);
    }
  }

  const alreadyExisted = result?.skippedRows.filter((s) => s.reason === 'already_exists').length ?? 0;
  const dupeInFile = result?.skippedRows.filter((s) => s.reason === 'duplicate_in_file').length ?? 0;

  return (
    <div data-tour="manage">
      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 13.5,
            color: c.danger,
          }}
        >
          {error}
        </div>
      )}

      {result && (
        <div
          style={{
            background: result.imported > 0 ? c.okBg : c.well,
            border: `1px solid ${result.imported > 0 ? '#BFD2C4' : c.border}`,
            borderRadius: 12,
            padding: '14px 18px',
            marginBottom: 16,
            fontSize: 13.5,
          }}
        >
          <strong style={{ color: result.imported > 0 ? c.success : c.ink }}>
            {result.imported > 0
              ? `✓ ${result.fileName}: imported ${result.imported} product${
                  result.imported === 1 ? '' : 's'
                }`
              : `${result.fileName}: no new products found`}
          </strong>
          <div style={{ color: c.muted2, marginTop: 6, lineHeight: 1.7 }}>
            {result.rows} row{result.rows === 1 ? '' : 's'} read
            {result.unitsAdded > 0 && ` · ${result.unitsAdded} units of opening stock`}
            {alreadyExisted > 0 &&
              ` · ${alreadyExisted} skipped because the name already exists`}
            {dupeInFile > 0 && ` · ${dupeInFile} repeated inside the file`}
          </div>
          {result.limitReached && (
            <div style={{ color: c.danger, marginTop: 8 }}>
              Your {result.limitReached.plan} plan tracks up to {result.limitReached.limit}{' '}
              products, so {result.limitReached.notImported} row
              {result.limitReached.notImported === 1 ? '' : 's'} could not be imported. Upgrade to
              add the rest.
            </div>
          )}
        </div>
      )}

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 6px' }}>
          Your products
        </h2>
        <p style={{ fontSize: 13.5, color: c.muted, margin: '0 0 16px', lineHeight: 1.7 }}>
          {mayEdit
            ? 'Costs, prices and reorder levels. Drag a product onto another type to move it, or use Edit. Changes are recorded in the audit log with the old and new value.'
            : 'Costs, prices and reorder levels. Ask a manager or the owner to change these.'}
        </p>

        {rows.length === 0 ? (
          <p style={{ fontSize: 13.5, color: c.muted2, margin: 0 }}>
            No products yet. Add one from the header, or import a spreadsheet below.
          </p>
        ) : (
          groupByClassification(rows, classifications).map((group) => (
            <div
              key={group.label}
              onDragOver={(e) => {
                if (!dragging || !group.id) return;
                e.preventDefault();
                setDropTarget(group.id);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropTarget(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                void dropOn(group.id);
              }}
              style={{
                marginBottom: 14,
                padding: '10px 12px 4px',
                borderRadius: 14,
                // Shown only while something is being dragged over this group.
                outline:
                  dropTarget !== null && dropTarget === group.id
                    ? `2px dashed ${c.green}`
                    : '2px dashed transparent',
                background:
                  dropTarget !== null && dropTarget === group.id ? '#EEF2EF' : 'transparent',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <h3 style={{ fontFamily: sans, fontSize: 13.5, fontWeight: 500, margin: 0 }}>
                  {group.label}
                </h3>
                <span style={{ fontSize: 12, color: c.muted2 }}>
                  {group.rows.length} product{group.rows.length === 1 ? '' : 's'} ·{' '}
                  {money(group.rows.reduce((n, r) => n + r.stockValuePence, 0))}
                </span>
              </div>
              {/* Fixed layout with shared column widths: each type is its own
                  table, and without this every table sized its columns from
                  its own contents, so they did not line up down the page. */}
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: 190 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={th}>Product</th>
                    <th style={th}>Unit cost</th>
                    <th style={th}>Retail</th>
                    <th style={th}>Reorder at</th>
                    <th style={th}>On hand</th>
                    <th style={{ ...th, textAlign: 'right' }} />
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row) => (
                    <ProductRowEditor
                      key={row.id}
                      row={row}
                      classifications={classifications}
                      categories={categories}
                      onDragStart={() => setDragging(row.id)}
                      onDragEnd={() => {
                        setDragging(null);
                        setDropTarget(null);
                      }}
                      mayEdit={mayEdit}
                      onSave={saveProduct}
                      onArchive={archive}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}

        {/* A type with no products has no table to drop on, so while something
            is being dragged each empty type appears as a place to drop it. */}
        {dragging !== null &&
          classifications
            .filter((cl) => !rows.some((r) => r.classificationId === cl.id))
            .map((cl) => (
              <div
                key={cl.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropTarget(cl.id);
                }}
                onDragLeave={() => setDropTarget(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  void dropOn(cl.id);
                }}
                style={{
                  marginBottom: 10,
                  padding: '14px 16px',
                  borderRadius: 14,
                  outline: `2px dashed ${dropTarget === cl.id ? c.green : c.borderStrong}`,
                  background: dropTarget === cl.id ? '#EEF2EF' : 'transparent',
                  fontSize: 13.5,
                  color: c.muted,
                }}
              >
                Drop here to move it to <strong style={{ color: c.ink }}>{cl.label}</strong>
              </div>
            ))}
      </div>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 16px' }}>
          Classifications and locations
        </h2>
        <TaxonomyEditor
          title="Classifications"
          noun="classification"
          blurb="How products are grouped on this page and across the app. A classification that still has products cannot be deleted."
          rows={classifications}
          mayEdit={mayEdit}
          onCreate={(labelText) => mutate(() => createClassification({ label: labelText }))}
          onRename={(id, labelText) => mutate(() => updateClassification(id, { label: labelText }))}
          onDelete={(id) => mutate(() => deleteClassification(id))}
        />
        <TaxonomyEditor
          title="Locations"
          noun="location"
          blurb="Where stock physically lives: a fridge, a treatment room, a cupboard. Chosen when you add stock."
          rows={locations}
          mayEdit={mayEdit}
          onCreate={(labelText) => mutate(() => createLocation({ label: labelText }))}
          onRename={(id, labelText) => mutate(() => updateLocation(id, { label: labelText }))}
          onDelete={(id) => mutate(() => deleteLocation(id))}
        />
      </div>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 6px' }}>
          Import from a spreadsheet
        </h2>
        <p style={{ fontSize: 13.5, color: c.muted, margin: '0 0 16px', lineHeight: 1.7 }}>
          CSV, .xlsx or .xls. Column headings are matched loosely, so
          &ldquo;Product&rdquo;, &ldquo;Item&rdquo; and &ldquo;Product name&rdquo; all work.
          Products whose name you already track are skipped, so running the same file twice is
          safe.
        </p>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={busy !== null}
            style={{
              ...primaryButton,
              width: 'auto',
              height: 44,
              padding: '0 20px',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy === 'import' ? 'Reading…' : 'Choose a file'}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => void onFile(e)}
            style={{ display: 'none' }}
          />

          <button
            type="button"
            onClick={() => void onTemplate()}
            disabled={busy !== null}
            style={{ ...smallButton, height: 44, padding: '0 18px' }}
          >
            {busy === 'template' ? 'Building…' : 'Download template'}
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 6px' }}>
          Export your stock
        </h2>
        <p style={{ fontSize: 13.5, color: c.muted, margin: '0 0 16px', lineHeight: 1.7 }}>
          {rows.length === 0
            ? 'Nothing to export yet.'
            : `${rows.length} product${rows.length === 1 ? '' : 's'} · ${money(
                rows.reduce((n, r) => n + r.stockValuePence, 0),
              )} of stock, with quantities, costs, values and expiry.`}
        </p>
        <button
          type="button"
          onClick={() => void onExport()}
          disabled={busy !== null || rows.length === 0}
          style={{
            ...smallButton,
            height: 44,
            padding: '0 18px',
            opacity: rows.length === 0 ? 0.5 : 1,
            cursor: rows.length === 0 ? 'not-allowed' : 'pointer',
            fontFamily: sans,
          }}
        >
          {busy === 'export' ? 'Building…' : 'Export to Excel'}
        </button>
      </div>
    </div>
  );
}
